import { useEffect, useState } from "react";
import { toast } from "sonner";
import { BookOpen, ChevronLeft, ChevronRight, ClipboardList, Plus } from "lucide-react";
import { useMe } from "@/context/MeContext";
import { useApi, errMsg } from "@/lib/api";
import { addDays, dayjs, fmtLong, fmtShort, nextSchoolDay, today, weekDates, weekday } from "@/lib/dates";
import { Card, ConfirmDelete, Empty, FormSheet, Loading, PageTitle, SubjectBadge } from "@/components/common";
import { Field, SubjectSelect } from "@/components/fields";
import { HomeworkItem, useHomeworkToggle } from "@/pages/Home";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";

const startDate = () => (weekday(today()) === 7 ? addDays(today(), 1) : today());

function DayStrip({ date, setDate }) {
  const days = weekDates(date);
  return (
    <div className="mb-5 flex items-center gap-1 fade-up">
      <Button variant="ghost" size="icon" className="h-11 w-9 shrink-0" onClick={() => setDate(addDays(days[0], -7))} data-testid="diario-prev-week" aria-label="Settimana precedente"><ChevronLeft className="h-5 w-5" /></Button>
      <div className="grid flex-1 grid-cols-6 gap-1.5" data-testid="select-date-diario">
        {days.map((d) => {
          const active = d === date;
          return (
            <button key={d} onClick={() => setDate(d)} data-testid={`diario-day-${d}`}
              className={`flex flex-col items-center rounded-xl py-2 transition-colors active:scale-95 ${active ? "bg-primary text-primary-foreground shadow-md" : "bg-card hover:bg-secondary"} ${d === today() && !active ? "ring-1 ring-primary" : ""}`}>
              <span className="text-[11px] font-semibold uppercase opacity-80">{dayjs(d).format("ddd")}</span>
              <span className="font-heading text-lg font-bold">{dayjs(d).format("D")}</span>
            </button>
          );
        })}
      </div>
      <Button variant="ghost" size="icon" className="h-11 w-9 shrink-0" onClick={() => setDate(addDays(days[0], 7))} data-testid="diario-next-week" aria-label="Settimana successiva"><ChevronRight className="h-5 w-5" /></Button>
    </div>
  );
}

function AddEntrySheet({ open, onOpenChange, kind, date, onSaved }) {
  const api = useApi();
  const [subject, setSubject] = useState("");
  const [text, setText] = useState("");
  const [due, setDue] = useState(nextSchoolDay(date));
  const [busy, setBusy] = useState(false);
  useEffect(() => { if (open) { setSubject(""); setText(""); setDue(nextSchoolDay(date)); } }, [open, date]);
  const isHw = kind === "homework";
  const save = async (e) => {
    e.preventDefault();
    if (!subject || !text.trim()) return toast.error("Compila materia e testo");
    setBusy(true);
    try {
      const body = isHw ? { subject, description: text, due_date: due } : { subject, topic: text, date };
      await api("post", isHw ? "/homework" : "/lessons", body);
      toast.success(isHw ? "Compito aggiunto" : "Argomento aggiunto");
      onOpenChange(false);
      onSaved();
    } catch (err) {
      toast.error(errMsg(err));
    } finally {
      setBusy(false);
    }
  };
  return (
    <FormSheet open={open} onOpenChange={onOpenChange} title={isHw ? "Nuovo compito" : "Nuovo argomento"} description={isHw ? "Assegnato il " + fmtShort(date) : "Lezione del " + fmtShort(date)}>
      <form onSubmit={save} className="space-y-4" data-testid="diario-entry-form">
        <Field label="Materia"><SubjectSelect value={subject} onChange={setSubject} testId="diario-entry-subject" /></Field>
        {isHw && <Field label="Per il giorno"><Input type="date" value={due} onChange={(e) => setDue(e.target.value)} className="h-11" data-testid="diario-entry-due" /></Field>}
        <Field label={isHw ? "Compito" : "Argomento della lezione"}>
          <Textarea rows={4} value={text} onChange={(e) => setText(e.target.value)} placeholder={isHw ? "Es. Esercizi pag. 120 n. 1-10" : "Es. Teorema di Pitagora e applicazioni"} data-testid="diario-entry-text" />
        </Field>
        <Button type="submit" disabled={busy} className="h-12 w-full rounded-full text-base" data-testid="diario-entry-submit">Salva</Button>
      </form>
    </FormSheet>
  );
}

export default function Diario() {
  const api = useApi();
  const { isAdmin } = useMe();
  const [date, setDate] = useState(startDate);
  const [lessons, setLessons] = useState(null);
  const [homework, setHomework] = useState(null);
  const [sheet, setSheet] = useState(null);
  const due = nextSchoolDay(date);
  const toggle = useHomeworkToggle(setHomework);

  const load = async () => {
    try {
      const [l, h] = await Promise.all([api("get", `/lessons?date=${date}`), api("get", `/homework?due=${due}`)]);
      setLessons(l);
      setHomework(h);
    } catch (e) {
      toast.error(errMsg(e));
    }
  };
  useEffect(() => { setLessons(null); setHomework(null); load(); }, [date]); // eslint-disable-line react-hooks/exhaustive-deps

  const remove = async (type, id) => {
    try {
      await api("delete", `/${type}/${id}`);
      toast.success("Eliminato");
      load();
    } catch (e) {
      toast.error(errMsg(e));
    }
  };

  const AddBtn = ({ kind, testId }) => isAdmin && (
    <Button size="sm" variant="secondary" className="rounded-full" onClick={() => setSheet(kind)} data-testid={testId}><Plus className="mr-1 h-4 w-4" />Aggiungi</Button>
  );

  return (
    <div>
      <PageTitle title="Diario di classe" subtitle={fmtLong(date)} />
      <DayStrip date={date} setDate={setDate} />
      <div className="grid gap-6 lg:grid-cols-2">
        <section data-testid="diario-lessons-section">
          <div className="mb-2.5 flex items-center justify-between">
            <h2 className="flex items-center gap-2 font-heading text-lg font-bold"><BookOpen className="h-5 w-5 text-primary" />Argomenti delle lezioni</h2>
            <AddBtn kind="lesson" testId="btn-add-lesson" />
          </div>
          {!lessons ? <Loading /> : lessons.length === 0 ? <Empty text="Nessun argomento per questo giorno." testId="diario-lessons-empty" /> : (
            <div className="space-y-2.5">
              {lessons.map((l) => (
                <Card key={l.id} className="flex items-start gap-2" data-testid={`lesson-card-${l.id}`}>
                  <div className="min-w-0 flex-1"><SubjectBadge subject={l.subject} /><p className="mt-1.5 whitespace-pre-line text-sm">{l.topic}</p></div>
                  {isAdmin && <ConfirmDelete testId={`lesson-delete-${l.id}`} onConfirm={() => remove("lessons", l.id)} />}
                </Card>
              ))}
            </div>
          )}
        </section>
        <section data-testid="diario-homework-section">
          <div className="mb-2.5 flex items-center justify-between">
            <h2 className="flex items-center gap-2 font-heading text-lg font-bold"><ClipboardList className="h-5 w-5 text-primary" />Compiti per {fmtShort(due)}</h2>
            <AddBtn kind="homework" testId="btn-add-homework" />
          </div>
          {!homework ? <Loading /> : homework.length === 0 ? <Empty text="Nessun compito per questo giorno." testId="diario-homework-empty" /> : (
            <Card className="space-y-1 p-2">
              {homework.map((h) => (
                <div key={h.id} className="flex items-start" data-testid={`homework-card-${h.id}`}>
                  <div className="min-w-0 flex-1"><HomeworkItem item={h} onToggle={toggle} /></div>
                  {isAdmin && <ConfirmDelete testId={`homework-delete-${h.id}`} onConfirm={() => remove("homework", h.id)} />}
                </div>
              ))}
            </Card>
          )}
        </section>
      </div>
      <AddEntrySheet open={!!sheet} onOpenChange={(o) => !o && setSheet(null)} kind={sheet} date={date} onSaved={load} />
    </div>
  );
}
